import { useSearchParams } from 'react-router-dom'
import { Download, ExternalLink, Mail, Monitor, Smartphone } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { CopyButton } from '@/components/shared/CopyButton'
import { AuthAlert } from '@/components/auth/AuthAlert'
import { WEBMAIL_URL } from '@/lib/constants'

const MAIL_HOST = 'mail.purpletoadmail.com'

function SettingRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="grid min-w-0 gap-1 rounded-lg bg-accent/40 px-3 py-2 sm:grid-cols-[8rem_minmax(0,1fr)_auto] sm:items-center sm:gap-3">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className="min-w-0 break-all font-mono text-sm">{value}</span>
      <CopyButton text={value} label={label} className="justify-self-end" />
    </div>
  )
}

export function ClientSetup() {
  const [searchParams] = useSearchParams()
  const email = (searchParams.get('email') || '').trim()
  const validEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
  const mobileConfigUrl = `/.well-known/mail/mobileconfig?email=${encodeURIComponent(email)}`
  const webmailUrl = `${WEBMAIL_URL}/login?email=${encodeURIComponent(email)}`

  if (!validEmail) {
    return (
      <div className="space-y-4">
        <h1 className="text-2xl font-bold tracking-tight">Set Up Your Mail App</h1>
        <AuthAlert message="This setup link is missing a valid mailbox email address. Ask your account administrator for a new link." />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="space-y-1.5 text-center">
        <h1 className="text-2xl font-bold tracking-tight">Set Up Your Mail App</h1>
        <p className="break-all text-sm text-muted-foreground">Use these settings for {email}</p>
      </div>

      <section className="space-y-3 rounded-xl border p-4">
        <div className="flex items-start gap-3">
          <Smartphone className="mt-0.5 h-5 w-5 shrink-0 text-violet-400" />
          <div>
            <h2 className="font-medium">Apple Mail</h2>
            <p className="text-xs text-muted-foreground">Download the profile, then review and install it in Settings on iPhone, iPad, or Mac.</p>
          </div>
        </div>
        <Button asChild className="w-full">
          <a href={mobileConfigUrl} download>
            <Download className="mr-2 h-4 w-4" />
            Download Apple profile
          </a>
        </Button>
      </section>

      <section className="space-y-3">
        <div className="flex items-start gap-3">
          <Monitor className="mt-0.5 h-5 w-5 shrink-0 text-violet-400" />
          <div>
            <h2 className="font-medium">Thunderbird and other mail apps</h2>
            <p className="text-xs text-muted-foreground">Choose manual setup or “Other account.” Use your mailbox password and enable authentication for outgoing mail.</p>
          </div>
        </div>
        <SettingRow label="Email / username" value={email} />
        <SettingRow label="IMAP server" value={MAIL_HOST} />
        <SettingRow label="IMAP port" value="993" />
        <SettingRow label="IMAP security" value="SSL/TLS" />
        <SettingRow label="SMTP server" value={MAIL_HOST} />
        <SettingRow label="SMTP port" value="587" />
        <SettingRow label="SMTP security" value="STARTTLS" />
      </section>

      <section className="space-y-2 border-t pt-4 text-center">
        <div className="flex items-center justify-center gap-2 text-sm font-medium">
          <Mail className="h-4 w-4 text-violet-400" />
          Need your inbox now?
        </div>
        <Button asChild variant="outline" className="w-full">
          <a href={webmailUrl}>
            Open Webmail
            <ExternalLink className="ml-2 h-4 w-4" />
          </a>
        </Button>
        <p className="text-xs text-muted-foreground">These settings are intended for Apple Mail, Thunderbird, and standards-based IMAP mail apps.</p>
      </section>
    </div>
  )
}
